import {
  SKILL_QUERY_MAX_LENGTH,
  SkillAudienceSchema,
  type SkillLifecycle,
  SkillLifecycleSchema,
} from '@parcel/types';
import type { EntityFilterSchema, FilterEnumValue } from '../types.js';

/** Deleted skills are never listable, so the filter vocabulary stops at three. */
export const SKILL_LIFECYCLE_VALUES = SkillLifecycleSchema.options.filter(
  (value): value is Exclude<SkillLifecycle, 'deleted'> => value !== 'deleted',
);

export const SKILL_LIFECYCLE_OPTIONS: readonly FilterEnumValue[] = [
  {
    value: 'draft',
    label: 'Draft',
    description:
      'Being written and never discoverable. Only the author, the maintainer, and workspace admins can read a draft.',
  },
  {
    value: 'published',
    label: 'Published',
    description:
      'The current revision is live and discoverable, so a Spec run may load it. This is the normal state of a working skill.',
  },
  {
    value: 'unpublished',
    label: 'Unpublished',
    description:
      'Withdrawn from discovery without being deleted. Its revisions survive and it can be published again.',
  },
];

export const SKILL_AUDIENCE_VALUES = SkillAudienceSchema.options;

export const SKILL_AUDIENCE_OPTIONS: readonly FilterEnumValue[] = [
  {
    value: 'personal',
    label: 'Personal',
    description: 'Belongs to one member. Nobody else in the workspace sees it or can load it.',
  },
  {
    value: 'workspace',
    label: 'Workspace',
    description: 'Shared with every member of the workspace, with one member as its maintainer.',
  },
];

export const SKILL_SOURCE_VALUES = ['custom', 'official'] as const;

export const SKILL_SOURCE_OPTIONS: readonly FilterEnumValue[] = [
  {
    value: 'custom',
    label: 'Custom',
    description: 'Authored in this workspace, either from scratch or forked from another skill.',
  },
  {
    value: 'official',
    label: 'Official',
    description:
      'Published by Parcel in the official catalog and installed into this workspace unchanged.',
  },
];

export const SKILL_INSTALLED_VALUES = ['installed', 'not_installed'] as const;

export const SKILL_INSTALLED_OPTIONS: readonly FilterEnumValue[] = [
  {
    value: 'installed',
    label: 'Installed',
    description:
      'Official skills installed into this workspace. Excludes every custom skill, which is never installed.',
  },
  {
    value: 'not_installed',
    label: 'Not installed',
    description:
      'Skills that were not installed from the official catalog, which is to say the custom ones.',
  },
];

/** Re-exported from @parcel/types so the filter entity, the routes and MCP share one bound. */
export { SKILL_QUERY_MAX_LENGTH };

export const skillFilters: EntityFilterSchema = {
  entity: 'skill',
  fields: [
    {
      type: 'text',
      key: 'q',
      label: 'Search',
      description: "Free-text search over the skill's name, slug, and description.",
      maxLength: SKILL_QUERY_MAX_LENGTH,
    },
    {
      type: 'enum',
      key: 'source',
      label: 'Source',
      description: 'Where the skill came from: authored here, or installed from the catalog.',
      multi: true,
      values: SKILL_SOURCE_OPTIONS,
    },
    {
      type: 'enum',
      key: 'lifecycle',
      label: 'Lifecycle',
      description: 'How far along the skill is: draft, live, or withdrawn from discovery.',
      multi: true,
      values: SKILL_LIFECYCLE_OPTIONS,
    },
    {
      type: 'enum',
      key: 'audience',
      label: 'Audience',
      description: 'Who the skill is for: one member, or the whole workspace.',
      multi: true,
      values: SKILL_AUDIENCE_OPTIONS,
    },
    {
      type: 'enum',
      key: 'installed',
      label: 'Installed',
      description: 'Whether the skill was installed from the official catalog.',
      multi: false,
      values: SKILL_INSTALLED_OPTIONS,
    },
    {
      type: 'date-range',
      key: 'updated',
      label: 'Updated',
      description: 'Date the skill was last changed, published, or installed.',
    },
  ],
};
